import * as React from 'react';
import { InfoMessage } from '../../hooks/useInfoMessages';
import moment from 'moment';
import 'react-datepicker/dist/react-datepicker.css';
import DatePicker from 'react-datepicker';
import * as i18next from 'i18next';
import { RouteComponentProps } from 'react-router';
import { Redirect } from 'react-router-dom';
import DefaultPromptModal from '../Controls/DefaultPromptModal';
import { defaultLanguage } from '../../constants/flags';
import Spinner from '../Controls/Spinner';
import { useInfoMessage, useAddInfoMessage, useEditInfoMessage, useDeleteInfoMessage } from '../../hooks/useInfoMessages';
import { MARKET } from '../../constants/storageKeys';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid2';

function AdminNewsView({ match, location, history }: RouteComponentProps<{ infoMessageId: string }>) {
    const infoMessageId = parseInt(match.params.infoMessageId);
    const isAddMode = infoMessageId === 0;
    const market = window.localStorage.getItem(MARKET) || 'se';

    const [infoMessage, setInfoMessage] = React.useState<InfoMessage>({
        infoMessageId: 0,
        message: '',
        showFromDateUTC: new Date(),
        showToDateUTC: undefined,
        updateDateUTC: new Date(),
        title: '',
        cultureId: defaultLanguage,
        lastUpdatedBy: ''
    });
    const [goBack, setGoBack] = React.useState(false);
    const [showDeleteModal, setShowDeleteModal] = React.useState(false);
    const [showSuccessDeleteModal, setShowSuccessDeleteModal] = React.useState(false);
    const [created, setCreated] = React.useState(false);
    const [edited, setEdited] = React.useState(false);
    const [triedToSend, setTriedToSend] = React.useState(false);
    const [titleValid, setTitleValid] = React.useState(true);
    const [messageValid, setMessageValid] = React.useState(true);
    const [showFromDateUTCValid, setShowFromDateUTCValid] = React.useState(true);
    const [showToDateUTCValid, setShowToDateUTCValid] = React.useState(true);

    const { data: fetchedMessage, isLoading } = useInfoMessage(isAddMode ? 0 : infoMessageId);
    const addMutation = useAddInfoMessage();
    const editMutation = useEditInfoMessage();
    const deleteMutation = useDeleteInfoMessage();

    React.useEffect(() => {
        if ((location.state as any)?.created === true) {
            setCreated(true);
        }
    }, [location.state]);

    React.useEffect(() => {
        if (fetchedMessage) {
            setInfoMessage(fetchedMessage);
            setTriedToSend(false);
        }
    }, [fetchedMessage]);

    React.useEffect(() => {
        if (editMutation.isSuccess && editMutation.data) {
            setInfoMessage(editMutation.data);
            setEdited(true);
            setCreated(false);
        }
    }, [editMutation.isSuccess]);

    React.useEffect(() => {
        if (deleteMutation.isSuccess) {
            setShowSuccessDeleteModal(true);
        }
    }, [deleteMutation.isSuccess]);

    if (goBack) return <Redirect to={'/administrationinfomessages'} />;
    if (isLoading) return <Spinner />;

    function checkValidity(info: InfoMessage): boolean {
        const tv = !!info.title;
        const mv = !!info.message;
        const fv = !!info.showFromDateUTC;
        setTitleValid(tv);
        setMessageValid(mv);
        setShowFromDateUTCValid(fv);
        setShowToDateUTCValid(true);
        return tv && mv && fv;
    }

    function sendInfoMessage(e: React.MouseEvent) {
        e.preventDefault();
        const info = { ...infoMessage, cultureId: defaultLanguage };
        setTriedToSend(true);
        if (!checkValidity(info)) return;
        if (isAddMode) {
            addMutation.mutate({ message: info, market }, {
                onSuccess: (id) => history.push('/administrationinfomessage/' + id, { created: true })
            });
        } else {
            editMutation.mutate(info);
        }
    }

    function handleDateChange(date: Date | null, id: string) {
        const updated = { ...infoMessage } as any;
        if (id === 'showToDateUTC' && date) {
            const now = new Date();
            date.setHours(now.getHours());
            date.setMinutes(now.getMinutes());
        }
        updated[id] = date ?? null;
        setInfoMessage(updated);
    }

    function handleInputChanges(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
        const updated = { ...infoMessage, [e.target.id]: e.target.value } as InfoMessage;
        setInfoMessage(updated);
    }

    function handleDeleteInfoMessage() {
        deleteMutation.mutate(infoMessage.infoMessageId);
        setShowDeleteModal(false);
    }

    return (
        <Box sx={{ maxWidth: 800 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
                {isAddMode ? i18next.t('newsadd.title') : i18next.t('newsedit.title')}
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {created && <Alert severity="success" sx={{ mb: 2 }}>{i18next.t('newsadd.messagesent')}</Alert>}
            {edited && <Alert severity="success" sx={{ mb: 2 }}>{i18next.t('newsedit.messagesent')}</Alert>}

            <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Grid container alignItems="center" spacing={2}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Typography component="label" htmlFor="title" fontWeight={500}>{i18next.t('newsedit.msgtitle')}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 8 }}>
                        <TextField
                            id="title"
                            fullWidth
                            size="small"
                            value={infoMessage.title || ''}
                            onChange={handleInputChanges}
                            error={!titleValid && triedToSend}
                            helperText={!titleValid && triedToSend ? i18next.t('newsedit.validtitle') : ''}
                        />
                    </Grid>
                </Grid>

                <Grid container alignItems="center" spacing={2}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Typography component="label" htmlFor="fromdate" fontWeight={500}>{i18next.t('newsedit.fromdate')}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 8 }}>
                        <DatePicker
                            id="fromdate"
                            className="form-control"
                            selected={infoMessage.showFromDateUTC ? new Date(infoMessage.showFromDateUTC) : null}
                            onChange={(date: Date | null) => handleDateChange(date, 'showFromDateUTC')}
                        />
                        {!showFromDateUTCValid && triedToSend &&
                            <Typography color="error" variant="caption">{i18next.t('newsedit.validfromdate')}</Typography>
                        }
                    </Grid>
                </Grid>

                <Grid container alignItems="center" spacing={2}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Typography component="label" htmlFor="todate" fontWeight={500}>{i18next.t('newsedit.todate')}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 8 }}>
                        <DatePicker
                            id="todate"
                            className="form-control"
                            selected={infoMessage.showToDateUTC ? new Date(infoMessage.showToDateUTC) : null}
                            onChange={(date: Date | null) => handleDateChange(date, 'showToDateUTC')}
                        />
                    </Grid>
                </Grid>

                <Grid container alignItems="flex-start" spacing={2}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Typography component="label" htmlFor="message" fontWeight={500}>{i18next.t('newsedit.message')}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 8 }}>
                        <TextField
                            id="message"
                            fullWidth
                            multiline
                            rows={8}
                            size="small"
                            value={infoMessage.message || ''}
                            onChange={handleInputChanges}
                            error={!messageValid && triedToSend}
                            helperText={!messageValid && triedToSend ? i18next.t('newsedit.validmessage') : ''}
                        />
                    </Grid>
                </Grid>

                <Divider />

                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Typography variant="body2" color="text.secondary">{i18next.t('common.lastupdated')}</Typography>
                        <Typography variant="body2">{infoMessage.updateDateUTC ? moment.utc(infoMessage.updateDateUTC).local().format('L HH:mm') : ''}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 8 }}>
                        <Typography variant="body2" color="text.secondary">{i18next.t('common.updatedby')}</Typography>
                        <Typography variant="body2">{infoMessage.lastUpdatedBy}</Typography>
                    </Grid>
                </Grid>

                <Divider />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    {!isAddMode && (
                        <Button
                            variant="contained"
                            color="error"
                            onClick={(e) => { e.preventDefault(); setShowDeleteModal(true); }}
                        >
                            {i18next.t('newsedit.remove')}
                        </Button>
                    )}
                    <Box sx={{ display: 'flex', gap: 1.5, ml: 'auto' }}>
                        <Button variant="contained" color="primary" onClick={sendInfoMessage}>
                            {isAddMode ? i18next.t('newsadd.button') : i18next.t('newsedit.button')}
                        </Button>
                        <Button variant="outlined" color="inherit" onClick={(e) => { e.preventDefault(); setGoBack(true); }}>
                            {i18next.t('newsedit.close')}
                        </Button>
                    </Box>
                </Box>
            </Box>

            <DefaultPromptModal show={showDeleteModal} handleClose={() => setShowDeleteModal(false)} handleSubmit={handleDeleteInfoMessage}
                header={i18next.t('newsdelete.header')} body={i18next.t('newsdelete.body') + ' ' + infoMessage.title + '?'}
                submitText={i18next.t('newsdelete.submittext')} closeText={i18next.t('newsdelete.closetext')} />
            <DefaultPromptModal show={showSuccessDeleteModal} handleClose={() => setGoBack(true)}
                header={i18next.t('newsdeletesuccess.header')} body={i18next.t('newsdeletesuccess.body') + ' ' + infoMessage.title + '!'}
                closeText={i18next.t('newsdeletesuccess.closetext')} />
        </Box>
    );
}

export default AdminNewsView;
